<template>
    <div>
        <McForm
            ref="wmFormRef"
            :model="innerValue"
            :rules="wmFormRules"
            :label-width="120"
        >
            <McFormItem
                label="商品类目"
                prop="channelBgCategoryId"
            >
                <WmCategorySelect
                    ref="wmCategorySelectRef"
                    v-model="innerValue.channelBgCategoryId"
                    :disabled="disabled"
                />
            </McFormItem>
        </McForm>
        <WmCategoryProperty
            ref="categoryPropertyRef"
            :properties="categoryProperties"
            :disabled="disabled"
            @change="handlePropertyChange"
        />
    </div>
</template>

<script>
import {
    computed, ref, watch, getCurrentInstance,
} from '@vue/composition-api';
import { hPost } from '@mcfe/tools';
import Rules from '@mcfe/tools-rules';
import WmCategorySelect from '../../../../flashSalesInfoManage/components/WmCategorySelect';
import WmCategoryProperty from '../../../../flashSalesInfoManage/components/WmCategoryProperty';

export default {
    components: {
        WmCategorySelect,
        WmCategoryProperty,
    },

    props: {
        value: {
            type: Object,
            default: () => ({}),
        },
        skuName: {
            type: String,
            default: '',
        },
        disabled: {
            type: Boolean,
            default: false,
        },
    },

    setup(props, { emit }) {
        const { proxy } = getCurrentInstance();
        const wmFormRef = ref(null);
        const wmCategorySelectRef = ref(null);
        const categoryPropertyRef = ref(null);
        const categoryProperties = ref([]);

        const wmFormRules = { channelBgCategoryId: [Rules.isRequired('请选择商品类目')] };

        const innerValue = computed({
            get() {
                return props.value;
            },
            set(val) {
                emit('input', val);
            },
        });

        // 获取类目层级
        const mateLevel = (val) => {
            const wmCategory = wmCategorySelectRef.value.mateWmCategory(val);
            return wmCategory?.level || '';
        };

        // 解析 childJson 字符串为对象
        const parseChildJson = (items) => {
            if (!Array.isArray(items)) return [];
            return items.map((item) => {
                if (item.childJson && typeof item.childJson === 'string') {
                    try {
                        item.childJson = JSON.parse(item.childJson);
                        // 递归解析子属性
                        if (item.childJson && item.childJson.length > 0) {
                            item.childJson = parseChildJson(item.childJson);
                        }
                    } catch (e) {
                        console.error('解析 childJson 失败', e);
                        item.childJson = [];
                    }
                }
                return item;
            });
        };

        // 合并扩展属性：将已有的属性值合并到新获取的属性结构中
        const mergeExtAttrs = (newAttrs, oldAttrs) => newAttrs.map((newItem) => {
            const oldItem = oldAttrs.find((item) => item.code === newItem.code);
            if (oldItem) {
                return {
                    ...newItem,
                    valueList: oldItem.valueList || [],
                    bindValue: oldItem.bindValue,
                    value: oldItem.value,
                };
            }
            return newItem;
        });

        // 查询外卖后台扩展属性
        const queryWmBgCategoryExtAttr = async (bgCategoryId) => {
            if (!bgCategoryId) return;
            try {
                const res = await hPost('/api/vss/shepherd/VssInternalChannelSkuTService/queryChannelBgCategoryExtAttr', { bgCategoryId, channelId: 4 });
                console.log('res', res);
                const extAttrs = parseChildJson(res.channelBgCategoryExtAttrs || []);
                // 如果是编辑或查看模式，需要合并已有的属性值
                if (props.disabled || innerValue.value?.channelExtAttrs?.length) {
                    const mergedAttrs = mergeExtAttrs(extAttrs, innerValue.value?.channelExtAttrs || []);
                    categoryProperties.value = mergedAttrs;
                } else {
                    categoryProperties.value = extAttrs;
                }
                console.log('categoryProperties', categoryProperties.value);
                // 将属性保存到 innerValue
                innerValue.value.channelExtAttrs = categoryProperties.value;
            } catch (err) {
                console.error('获取类目属性失败', err);
            }
        };

        // 类目change
        watch(() => innerValue.value?.channelBgCategoryId, (newValue) => {
            if (!newValue) return;
            // 重新获取类目属性
            queryWmBgCategoryExtAttr(newValue);
            // 更新类目层级
            innerValue.value.channelBgCategoryLevel = mateLevel(newValue);
        });

        // 初始化：如果已有类目ID，则加载属性
        if (innerValue.value?.channelBgCategoryId) {
            queryWmBgCategoryExtAttr(innerValue.value.channelBgCategoryId);
        }

        // 处理属性变化
        const handlePropertyChange = (item) => {
            console.log('属性变化:', item);
        };

        // 验证表单和扩展属性
        const validate = async () => {
            // 先验证表单字段（包括商品类目）
            const validateForm = wmFormRef.value.validate().then(() => true).catch((err) => {
                console.warn('表单校验失败:', err);
                return false;
            });

            const formValid = await validateForm;
            // 如果表单验证失败，立即返回 false
            if (!formValid) {
                return false;
            }

            // 再验证扩展属性（类目属性）
            let validateExtAttr = true;
            if (categoryPropertyRef.value) {
                const result = categoryPropertyRef.value.validate();
                if (result !== true) {
                    validateExtAttr = false;
                    if (Array.isArray(result) && result.length > 0) {
                        proxy.$mc.message.error(result[0]);
                    }
                }
            }

            return validateExtAttr;
        };

        // 将 childJson 转换回字符串格式用于提交
        const stringifyChildJson = (items) => {
            if (!Array.isArray(items)) return [];
            return items.map((item) => {
                const newItem = { ...item };
                if (newItem.childJson && Array.isArray(newItem.childJson)) {
                    newItem.childJson = JSON.stringify(stringifyChildJson(newItem.childJson));
                }
                // 移除不需要的字段
                delete newItem.bindValue;
                delete newItem.value;
                return newItem;
            });
        };

        // 获取提交数据
        const getVMPostData = () => {
            const postData = {
                channelId: innerValue.value.channelId || 4,
                channelBgCategoryId: innerValue.value.channelBgCategoryId,
                channelBgCategoryLevel: innerValue.value.channelBgCategoryLevel,
                channelExtAttrs: stringifyChildJson(categoryProperties.value),
            };
            return postData;
        };

        return {
            wmFormRef,
            wmCategorySelectRef,
            categoryPropertyRef,
            categoryProperties,
            innerValue,
            wmFormRules,
            queryWmBgCategoryExtAttr,
            mateLevel,
            validate,
            handlePropertyChange,
            getVMPostData,
        };
    },
};
</script>
